import type { ModelFace, ModelFileType, ModelStore } from "@centricsoftware/model-viewer";
import { IModelFileType } from "@centricsoftware/model-viewer";
import crews from "csi/access/crews";
import comm from "csi/comm";
import JsonResponse from "csi/comm/JsonResponse";
import { TYPE_MATERIALDRAPE, TYPE_U3M, getFromThreeDConfig, getModelFile, getModelFileUrl } from "csi/model/document";
import localeProvider from "csi/page/localeProvider";
import { allow3dAnnotations } from "csi/sessionConstants";
import { SnapshotTemplateId } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { decryptFileChunks } from "csi/threeD/utility/avatarDecrypt";
import { C8Node, C8NodeType, ICache, cnl } from "csi/typings";
import { parseHexAlpha } from "csi/utility/colorUtility";
import { getFileNameExtension } from "csi/utility/fileUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { TRANSPARENT } from "designSystem/components/colorPicker/ColorPicker";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import Cache from "../Cache";
import { localStore } from "../rootStore";
import AnnotationsStore from "./annotationsStore";
import CanvasStore from "./canvasStore";
import EnvironmentsStore from "./environmentsStore";
import MaterialSettingsStore, { DEFAULT_MATERIAL_GEOMETRY } from "./materialSettingsStore";
import SnapshotsStore from "./snapshotsStore";

const DEFAULT_THREED_VIEWER_ID = "3DViewer";
const DEFAULT_VIEWER_BACKGROUND_COLOR = "#ffffff";

export type SnapshotSaveLocationData = {
	/** all possible locations that a user can save their snapshots too */
	locations: C8Node[];

	/** recommended locations that a user can save their snapshots too. Locations are determined based off of where the user launched the 3D viewer from */
	suggestedLocations: string[];
}

export class ThreeDStore extends ReactionStore {
	@observable private _viewerId: string = "";

	/** title of 3D panel */
	@observable threeDTitle: string;

	/** Product type of root document */
	@observable productType: C8NodeType | undefined;

	/** Type of the root document */
	@observable rootNodeType: string;

	/** location title for the model */
	@observable locationTitle: string = "";

	/** Reference to the store returned from modelViewer */
	@observable.ref modelViewerStore: ModelStore | undefined = undefined;

	/** reset color picker flag */
	@observable resetColorPickerToDefault = false;

	/** cache of 3D doc Nodes*/
	cache: Cache = new Cache();

	/** Stores document url */
	docUrl: cnl;

	/** Stores document revision */
	revisionNode: C8Node;

	/** type of domain object owning the revision */
	primaryType: string;

	/** url of domain object owning the revision*/
	primaryUrl: string;

	/** store for environment lighting */
	environmentsStore: EnvironmentsStore;

	/** store for snapshots */
	snapshotsStore: SnapshotsStore;

	/** store for canvas state */
	canvasStore: CanvasStore;

	/** store for annotations */
	annotationsStore: AnnotationsStore;

	/** store for material panel */
	materialSettingsStore: MaterialSettingsStore;
	
	/** locale provider to be used in stores */
	localeProvider: ILocaleProvider = null;

	/** model File to be displayed in the viewer */
	@observable modelFile: ModelFileType;

	/** all environment urls */
	@observable.ref environmentUrls: cnl[] = [];

	/** return if viewer is 3D Style Viewer */
	is3DViewer = (): boolean => {
		return this._viewerId === DEFAULT_THREED_VIEWER_ID;
	};

	/** Snapshot Template ID */
	get snapshotTemplateId(): SnapshotTemplateId {
		const isMaterial = this.rootNodeType === "Material" || this.rootNodeType === "ColorMaterial";
		return isMaterial ? SnapshotTemplateId.Material : SnapshotTemplateId.Style;
	}
	
	/** check to see if user has 3D Library Admin role */
	get has3DLibraryAdminRole(): boolean {
		return crews.isInGlobalRoles(["3D Library Administrator"]);
	}

	/** returns whether or not pins are allowed- currently pins are not allowed on u3m material types (trims, artwork, seams, fabrics) nor are they allowed when viewing physics files */
	get allowPins(): boolean {
		const isPhysics = this.revisionNode.$Type === TYPE_MATERIALDRAPE;
		return allow3dAnnotations && !!this.is3DViewer() && !isPhysics;
	}

	/** returns the document node */
	get docNode(): C8Node {
		return this.cache.get(this.docUrl);
	}

	/** category to filter by */
	get viewerId(): string {
		return this._viewerId || DEFAULT_THREED_VIEWER_ID;
	}

	/** all model file urls */
	get modelFiles(): ComponentItem[] | null {
		// if the model is encrypted then return null as the model file is an ArrayBuffer
		const { isEncrypted } = this;
		if (isEncrypted) {
			return null;
		}
		const allModels = getModelFileUrl(this.revisionNode);
		return allModels.map((file) => ({ id: file }));
	}

	/** set the modelViewer file */
	@action setModelViewerFile = (modelFile: ComponentItem): void => {
		this.modelFile = modelFile.id;

		// If the model file is changed, reset the default material geometry
		this.materialSettingsStore.setActiveMaterialGeometry(DEFAULT_MATERIAL_GEOMETRY);
		this.modelViewerStore?.resetCamera();
		this.modelViewerStore?.resetControlsTarget();

		// If the model file is changed, reset the generate images options 
		this.snapshotsStore.clearGenerateImageOptions();
	};

	/** set the modelViewer store */
	@action setModelViewerStore = (modelViewerStore: ModelStore): void => {
		this.modelViewerStore = modelViewerStore;
	};

	/** model file type */
	get fileType(): IModelFileType {
		// if modelFile is an ArrayBuffer then we know that we are dealing with a glb file as that is the only file type that can be chunked
		const { isEncrypted, modelFile } = this;

		if (isEncrypted) {
			return "glb";
		}

		if (this.revisionNode.$Type === TYPE_MATERIALDRAPE) {
			return "obj";
		}

		return getFileNameExtension(modelFile as string) as IModelFileType;
	}

	/** u3m type */
	get u3mType(): string | null {
		return this.fileType == "u3m" ? getFromThreeDConfig(TYPE_U3M, this.revisionNode) : null;
	}

	/** Default Model File to display if there are more than one (i.e grouped u3ma files) */
	get defaultModelFile(): string | undefined {
		const { modelFile, modelFiles } = this;

		if (!modelFile || modelFile instanceof ArrayBuffer) {
			return;
		}

		return modelFile || modelFiles[0]["id"];
	}

	/** determine if the modelFile is encrypted */
	private get isEncrypted(): boolean {
		const fileChunks = this.revisionNode.FileChunks;
		const isChunked = !!fileChunks?.trim();

		return isChunked;
	}

	/** return the model URL to be used for ModelViewer */
	get modelUrl(): ModelFileType {
		const { modelFile, revisionNode } = this;

		if (modelFile instanceof ArrayBuffer) {
			return modelFile;
		}
		const file = getModelFile(revisionNode, modelFile);
		const modelUrl = getFileVaultRequestUrl(file, false);

		return modelUrl;
	}

	/** change pane title */
	@action setTitle = (title: string): void => {
		this.threeDTitle = title;
	};

	/** set the location title */
	@action setLocationTitle = (title: string): void => {
		this.locationTitle = title;
	};

	/** Toggle Axis helper & turn off polar lock */
	@action toggleAxis = (): void => {
		const {
    		lockPolarAngle,
    		toggleAxisHelper,
    		toggleLockPolarRotation,
    	} = this.modelViewerStore;

		toggleAxisHelper();

		if (lockPolarAngle) {
			toggleLockPolarRotation();
		}
	};

	/** Select Model Faces & turn off polar lock */
	@action handleSelectFace = (face: ModelFace): void => {
		const { 
			lockPolarAngle,
			toggleLockPolarRotation,
			setModelFace,
		} = this.modelViewerStore;
			
		if (lockPolarAngle) {
			toggleLockPolarRotation();
		}

		setModelFace(face);
	};

    /** set the viewer background color- this is wrapped in a setTimeout to ensure that background color is set after the current event loop to avoid ModelViewer using the previous background color*/
    @action setModelViewerBackground = (color: string): void => {
    	const { rgb, alpha } = color === TRANSPARENT ? { rgb: "#ffffff", alpha: 0 } : parseHexAlpha(color);	
    	setTimeout(() => {
    		this.modelViewerStore.setBackgroundColor(rgb);
    		this.modelViewerStore.setBackgroundAlpha(alpha);
    	}, 0);
    };

    /** Update the viewer background and update the environment background */
    @action addBackgroundColor = async (color: string): Promise<void> => {
    	const { updateEnvironmentBackground } = this.environmentsStore;
    	updateEnvironmentBackground(false);
    	this.setModelViewerBackground(color);
    };

	/** Reset the color picker to default color */
	@action resetColorPicker = (): void => {
		this.resetColorPickerToDefault = !this.resetColorPickerToDefault;
	};

    /** Reset the style viewer to its original view */
    @action resetViewer = (): void => {
    	const { modelViewerStore, setModelViewerBackground, resetColorPicker } = this;
    	const { resetToDefaultLighting } = this.environmentsStore;
    	const {
    		axisHelperVisible,
    		lockPolarAngle,
    		resetCamera,
    		toggleAxisHelper,
    		toggleLockPolarRotation,
    		resetControlsTarget,
    	} = modelViewerStore;
        
    	resetCamera();
    	resetControlsTarget();
    	setModelViewerBackground(DEFAULT_VIEWER_BACKGROUND_COLOR);
    	resetColorPicker();
    	resetToDefaultLighting();

    	if (axisHelperVisible) {
    		toggleAxisHelper();
    	}

    	if (lockPolarAngle) {
    		toggleLockPolarRotation();
    	}
    };

    /** async fetching of the location title path  */
    fetchLocationTitle = async (): Promise<string> => {
    	const modelUrl = this.revisionNode?.$URL || this.docNode.$URL;
    	const revisionUrl = this.revisionNode.$URL;
    	const merchSeasonUrl = localStore.merch.seasonUrl;
    	let response: JsonResponse;
    	let breadcrumbUrls: cnl[];

    	const locationRequest = comm.getBreadcrumbRequest(modelUrl, revisionUrl, merchSeasonUrl);
    	try {
    		response = await locationRequest.go();
    		breadcrumbUrls = response.getResultNodes()
    			.reverse()
    			.filter((node: C8Node) => node.$Type !== "DocumentContainer" && node.$Type !== "DocumentRevision" && node.$Type !== "Document")
    			.map((node: C8Node) => node.$Name);
    		return breadcrumbUrls.join(" / ");
    	} catch (err){
    		throw new Error(err);
    	}
    };

	/** async fetching of Environment Urls */
	@action fetchEnvironmentUrls = async (): Promise<void> => {
		try {
			const search = new Search()
				.byType("ThreeDEnvironment");
			const query = search.getQuery();
			query.Attr("Active", xml.OP_EQUALS, true);
			search.getSortQuery()
				.SortByNode(xml.PROP_CREATE_TIME, null, true);

			const response = await search.getRequest()
				.go();

			runInAction(() => {
				this.environmentUrls = response.getResultUrls();
				this.cache.cacheResponse(response);
			});
		} catch (err) {
			console.error("fetchEnvironmentUrls: ", err);
		}
	};

	/** Inialization Function to provide to modelViewer before initial render */
	@action initializeModelViewer = (store: ModelStore): void => {
		const {
			changeModelSectionVisibility,
			generateImages,
			modelData,
			setEnvironmentBackground,
			setEnvironmentMap,
			setEnvironmentOrientation,
			setToneMappingExposure,
			setAnnotations,
			setAnnotationsVisible,
			setEditingAnnotation,
			setUpdateAnnotationPosition,
			setOnAnnotationUpdate,
			setOnAnnotationClick,
			setOnAnnotationOver,
			setOnAnnotationOut,
			cancelEditingAnnotation,
			modelVariants,
			numberOfGenerateImages,
			updateCurrentVariant,
			setActiveMaterialFace,
			setMaterialGeometry,
			materialPrimitives,
		} = store;
		const { canvasStore, environmentsStore, annotationsStore, materialSettingsStore, snapshotsStore } = this;
	
		this.setModelViewerStore(store);
	
		environmentsStore.modelViewerUpdateEnvironment = setEnvironmentMap;
		environmentsStore.modelViewerUpdateExposure = setToneMappingExposure;
		environmentsStore.modelViewerUpdateOrientation = setEnvironmentOrientation;
		environmentsStore.modelViewerUpdateBackground = setEnvironmentBackground;
	
		canvasStore.changeModelSectionVisibility = changeModelSectionVisibility;
		canvasStore.onChangeModelVariant = updateCurrentVariant;
		canvasStore.setModelVariants(modelVariants); 
		canvasStore.setMeshData(modelData);
	
		if (modelVariants) {
			canvasStore.onChangeModelVariant(canvasStore.defaultVariantName);
		}
	
		annotationsStore.modelViewerSetEditingAnnotation = setEditingAnnotation;
		annotationsStore.modelViewerSetUpdateAnnotationPosition = setUpdateAnnotationPosition;
		annotationsStore.modelViewerCancelEditingAnnotation = cancelEditingAnnotation;
		annotationsStore.modelViewerSetAnnotations = setAnnotations;
	
		materialSettingsStore.modelViewerSetActiveMaterialFace = setActiveMaterialFace;
		materialSettingsStore.modelViewerSetMaterialGeometry = setMaterialGeometry;
		materialSettingsStore.setMaterialPrimitives(materialPrimitives);
	
		snapshotsStore.modelViewerNumberOfGenerateImages = numberOfGenerateImages;
		snapshotsStore.modelViewerGenerateImages = generateImages;
		
		setAnnotationsVisible(false);
		setOnAnnotationUpdate((annotation) => {
			annotationsStore.setPinPosition({
				x: annotation.position[0],
				y: annotation.position[1],
				z: annotation.position[2],
			});
		});
		setOnAnnotationClick((annotation) => {
			const commentUrl = annotationsStore.findCommentUrlFromAnnotation(annotation);
			annotationsStore.onPinClick(commentUrl);
		});
		setOnAnnotationOver((annotation) => {
			const commentUrl = annotationsStore.findCommentUrlFromAnnotation(annotation);
			annotationsStore.onPinHover(commentUrl);
		});
		setOnAnnotationOut(() => {
			annotationsStore.onPinHover(null);
		});
	};

	/** async process to initialize store */
	private initialize = async (): Promise<void> => {
		const { fetchLocationTitle, revisionNode, isEncrypted } = this;
		await this.fetchEnvironmentUrls();
		const title = await fetchLocationTitle();
		this.setLocationTitle(title);

		if (isEncrypted) {
			const fileChunks = revisionNode.FileChunks;
			const decryptedChunks = await decryptFileChunks(fileChunks);
			this.modelFile = decryptedChunks;
		}
	};

	constructor(
		primary: C8Node,
		revisionNode: C8Node,
		onUploadSnapshot: VoidFunction,
		snapshotSaveLocationsData: SnapshotSaveLocationData,
		viewerId: string,
		sourceViewCache: ICache, 
		threeDTitle: string, 
		variantName? : string,
		productType?: C8NodeType,
		rootNodeType?: C8NodeType,
	) {
		super();
		makeObservable(this);

		const { cache } = this;
		const { $Type, $URL } = primary;
		this.primaryType = $Type;
		this.primaryUrl = $URL;
		this.docUrl = primary.$URL;
		this.revisionNode = revisionNode;
		this._viewerId = viewerId;
		this.localeProvider = localeProvider;
		this.productType = productType;
		this.rootNodeType = rootNodeType;
		
		cache.add(primary);		

		this.threeDTitle = threeDTitle;

		// Initalize the 3D feature stores
		this.canvasStore = new CanvasStore(variantName);
		this.environmentsStore = new EnvironmentsStore(this.canvasStore, sourceViewCache, this.cache, this.docUrl);
		this.materialSettingsStore = new MaterialSettingsStore(revisionNode, this.canvasStore);
		this.snapshotsStore = new SnapshotsStore(this.canvasStore, this.materialSettingsStore, this.environmentsStore, revisionNode, onUploadSnapshot, snapshotSaveLocationsData, this.threeDTitle, this.localeProvider);
		this.annotationsStore = new AnnotationsStore(this.canvasStore);

		// get the breadcrumb path for the document & decrypt the file chunks if necessary
		this.initialize();
		if (!this.isEncrypted) {
			this.modelFile = this.modelFiles[0].id;
		}

		this.trackReaction(reaction(
			() => this.annotationsStore.pinIsMoving,
			(value) => {
				if (value) {
					this.modelViewerStore?.setAnnotationsVisible(true);
				}
			},
		));

		this.trackReaction(reaction(
			() => this.modelViewerStore?.materialPrimitives,
			(value) => {
				this.materialSettingsStore.modelViewerMaterialPrimitives = value;
			},
		));

		this.trackReaction(reaction(
			() => [this.environmentUrls, this.modelViewerStore],
			() => {
				if (this.environmentUrls.length > 0 && this.modelViewerStore) {
					const defaultURL = this.environmentsStore.defaultEnvironmentURL;
					this.environmentsStore.handleEnvironmentSelectChange(null, defaultURL);
				}
			},
		));
	}
}

export default ThreeDStore;